/**
 * Voice Agent Config Page
 * Manage ElevenLabs agent configuration, A/B test variants, call history, and performance metrics.
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Phone,
  RefreshCw,
  Edit2,
  Save,
  X,
  Plus,
  CheckCircle,
  AlertTriangle,
  Clock,
  TrendingUp,
  Mic,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  useAgentConfig,
  useAgentCalls,
  useAgentMetrics,
  type CallDetail,
} from '@/hooks/useAgentConfig';
import { useAuth } from '@/lib/auth/AuthProvider';

function OutcomeTag({ outcome }: { outcome: string | null }) {
  if (!outcome) return <Badge variant="secondary">Unknown</Badge>;
  const variants: Record<string, string> = {
    meeting_booked: 'bg-green-500/20 text-green-600 border-green-500/30',
    qualified: 'bg-blue-500/20 text-blue-600 border-blue-500/30',
    not_interested: 'bg-red-500/20 text-red-600 border-red-500/30',
    voicemail: 'bg-yellow-500/20 text-yellow-600 border-yellow-500/30',
    no_answer: 'bg-gray-500/20 text-gray-500 border-gray-500/30',
  };
  const cls = variants[outcome] || 'bg-gray-500/20 text-gray-500 border-gray-500/30';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cls}`}>
      {outcome.replace(/_/g, ' ')}
    </span>
  );
}

function formatDuration(secs: number | null) {
  if (secs === null || secs === 0) return '—';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function MetricsPanel() {
  const { metrics, loading, error, fetchMetrics } = useAgentMetrics();

  useEffect(() => { fetchMetrics(30); }, [fetchMetrics]);

  if (loading) return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
    </div>
  );

  if (error) return (
    <Card className="border-yellow-500/50 bg-yellow-500/10">
      <CardContent className="py-3 text-sm text-yellow-600">{error}</CardContent>
    </Card>
  );

  if (!metrics) return null;

  const statCards = [
    { label: 'Total Calls (30d)', value: metrics.total_calls.toString(), icon: Phone },
    { label: 'Qualification Rate', value: `${metrics.qualification_rate}%`, icon: TrendingUp },
    { label: 'Booking Rate', value: `${metrics.booking_rate}%`, icon: CheckCircle },
    { label: 'Avg Duration', value: formatDuration(metrics.avg_duration_secs), icon: Clock },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {statCards.map(({ label, value, icon: Icon }) => (
        <Card key={label}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <Icon className="h-4 w-4" /> {label}
            </div>
            <div className="text-2xl font-bold">{value}</div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function CallHistoryPanel() {
  const { calls, loading, error, fetchCalls, fetchTranscript } = useAgentCalls();
  const [selectedTranscript, setSelectedTranscript] = useState<CallDetail | null>(null);
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [expandedCall, setExpandedCall] = useState<string | null>(null);

  useEffect(() => { fetchCalls(50); }, [fetchCalls]);

  const handleExpandCall = async (conversationId: string) => {
    if (expandedCall === conversationId) {
      setExpandedCall(null);
      setSelectedTranscript(null);
      return;
    }
    setExpandedCall(conversationId);
    setTranscriptLoading(true);
    try {
      const detail = await fetchTranscript(conversationId);
      setSelectedTranscript(detail);
    } catch {
      toast.error('Failed to load transcript');
    } finally {
      setTranscriptLoading(false);
    }
  };

  if (loading) return (
    <div className="space-y-2">
      {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16" />)}
    </div>
  );

  if (error) return (
    <Card className="border-yellow-500/50 bg-yellow-500/10">
      <CardContent className="py-3 text-sm text-yellow-600">{error}</CardContent>
    </Card>
  );

  if (calls.length === 0) return (
    <Card>
      <CardContent className="py-8 text-center text-muted-foreground">
        No AI calls recorded yet. Calls will appear here after the first outbound AI call completes.
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-2">
      {calls.map((call) => (
        <Card key={call.conversation_id} className="overflow-hidden">
          <button
            className="w-full text-left"
            onClick={() => handleExpandCall(call.conversation_id)}
          >
            <CardContent className="py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {expandedCall === call.conversation_id
                    ? <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    : <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  }
                  <div>
                    <div className="font-medium text-sm">
                      {call.prospect_name || 'Unknown'}
                      {call.company_name && (
                        <span className="text-muted-foreground font-normal"> — {call.company_name}</span>
                      )}
                    </div>
                    {call.summary && (
                      <div className="text-xs text-muted-foreground truncate max-w-md">{call.summary}</div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4 text-sm flex-shrink-0">
                  <OutcomeTag outcome={call.outcome} />
                  <span className="text-muted-foreground">{formatDuration(call.duration_secs)}</span>
                  <span className="text-muted-foreground text-xs">
                    {call.completed_at
                      ? new Date(call.completed_at).toLocaleDateString()
                      : '—'}
                  </span>
                </div>
              </div>
            </CardContent>
          </button>

          {expandedCall === call.conversation_id && (
            <div className="border-t bg-muted/30 p-4">
              {transcriptLoading ? (
                <Skeleton className="h-32" />
              ) : selectedTranscript ? (
                <div className="space-y-3">
                  {selectedTranscript.analysis?.summary && (
                    <div>
                      <div className="text-xs font-medium text-muted-foreground mb-1">SUMMARY</div>
                      <p className="text-sm">{selectedTranscript.analysis.summary}</p>
                    </div>
                  )}
                  <div>
                    <div className="text-xs font-medium text-muted-foreground mb-1">TRANSCRIPT</div>
                    <pre className="text-sm whitespace-pre-wrap font-sans bg-background border rounded-md p-3 max-h-80 overflow-y-auto">
                      {selectedTranscript.transcript || 'No transcript available'}
                    </pre>
                  </div>
                  {selectedTranscript.recording_url && (
                    <div className="text-xs text-muted-foreground">
                      Recording: <code>{selectedTranscript.recording_url}</code>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}

function VariantsPanel({
  config,
  onCreateVariant,
  onActivateVariant,
}: {
  config: ReturnType<typeof useAgentConfig>['config'];
  onCreateVariant: (name: string, prompt: string, firstMessage?: string) => Promise<void>;
  onActivateVariant: (name: string) => Promise<void>;
}) {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPrompt, setNewPrompt] = useState('');
  const [newFirstMessage, setNewFirstMessage] = useState('');
  const [saving, setSaving] = useState(false);

  if (!config) return null;

  const allVariants = ['main', ...Object.keys(config.variants).filter(k => k !== 'main')];

  const handleCreate = async () => {
    if (!newName.trim() || !newPrompt.trim()) {
      toast.error('Variant name and prompt are required');
      return;
    }
    setSaving(true);
    try {
      await onCreateVariant(newName.trim(), newPrompt, newFirstMessage || undefined);
      toast.success(`Variant '${newName}' created`);
      setCreating(false);
      setNewName('');
      setNewPrompt('');
      setNewFirstMessage('');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to create variant');
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async (name: string) => {
    try {
      const { synced, syncError } = await onActivateVariant(name);
      if (syncError) {
        toast.warning(`Variant activated locally. ElevenLabs sync failed: ${syncError}`);
      } else if (synced) {
        toast.success(`Variant '${name}' activated and synced to ElevenLabs`);
      } else {
        toast.success(`Variant '${name}' activated (ElevenLabs not configured)`);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to activate variant');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Prompt Variants</h3>
        <Button variant="outline" size="sm" onClick={() => setCreating(true)} disabled={creating}>
          <Plus className="h-4 w-4 mr-1" /> New Variant
        </Button>
      </div>

      {allVariants.map((name) => {
        const isActive = config.active_variant === name;
        const variantData = name === 'main'
          ? { system_prompt: config.system_prompt, first_message: config.first_message }
          : config.variants[name];

        return (
          <Card key={name} className={isActive ? 'border-primary' : ''}>
            <CardContent className="py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm">{name}</span>
                    {isActive && <Badge className="text-xs">Active</Badge>}
                    {name === 'main' && !isActive && (
                      <Badge variant="secondary" className="text-xs">base</Badge>
                    )}
                    {variantData?.['created_at'] && (
                      <span className="text-xs text-muted-foreground">
                        {new Date(variantData['created_at'] as string).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  {variantData?.system_prompt && (
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {variantData.system_prompt}
                    </p>
                  )}
                </div>
                {!isActive && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleActivate(name)}
                  >
                    Activate
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {creating && (
        <Card className="border-dashed">
          <CardContent className="pt-4 space-y-3">
            <div>
              <Label htmlFor="variant-name" className="text-xs">Variant Name</Label>
              <Input
                id="variant-name"
                placeholder="e.g. v2_consultative"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">Alphanumeric + underscores only</p>
            </div>
            <div>
              <Label htmlFor="variant-prompt" className="text-xs">System Prompt</Label>
              <Textarea
                id="variant-prompt"
                placeholder="You are a sales agent for BuildGravity..."
                value={newPrompt}
                onChange={e => setNewPrompt(e.target.value)}
                className="mt-1 font-mono text-xs"
                rows={6}
              />
            </div>
            <div>
              <Label htmlFor="variant-first-message" className="text-xs">First Message (optional)</Label>
              <Input
                id="variant-first-message"
                placeholder="Hi, is this {{prospect_name}}?"
                value={newFirstMessage}
                onChange={e => setNewFirstMessage(e.target.value)}
                className="mt-1"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setCreating(false)}>Cancel</Button>
              <Button size="sm" onClick={handleCreate} disabled={saving}>
                {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                Create
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ConfigEditorPanel({ config, onSave }: {
  config: ReturnType<typeof useAgentConfig>['config'];
  onSave: (update: { system_prompt?: string; first_message?: string; dynamic_variables_template?: Record<string, string>; tools?: Record<string, unknown>[] }) => Promise<{ synced: boolean; syncError: string | null }>;
}) {
  const [editMode, setEditMode] = useState(false);
  const [editPrompt, setEditPrompt] = useState('');
  const [editFirstMessage, setEditFirstMessage] = useState('');
  const [editDynamicVars, setEditDynamicVars] = useState('');
  const [editTools, setEditTools] = useState('');
  const [saving, setSaving] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);

  const startEdit = () => {
    setEditPrompt(config?.system_prompt || '');
    setEditFirstMessage(config?.first_message || '');
    setEditDynamicVars(JSON.stringify(config?.dynamic_variables_template || {}, null, 2));
    setEditTools(JSON.stringify(config?.tools || [], null, 2));
    setJsonError(null);
    setEditMode(true);
  };

  const handleSave = async () => {
    setJsonError(null);

    let parsedVars: Record<string, string> | undefined;
    let parsedTools: Record<string, unknown>[] | undefined;

    try {
      parsedVars = JSON.parse(editDynamicVars);
    } catch {
      setJsonError('Dynamic Variables JSON is invalid');
      return;
    }

    try {
      parsedTools = JSON.parse(editTools);
      if (!Array.isArray(parsedTools)) throw new Error('Tools must be an array');
    } catch {
      setJsonError('Tools JSON is invalid (must be an array)');
      return;
    }

    setSaving(true);
    try {
      const { synced, syncError } = await onSave({
        system_prompt: editPrompt,
        first_message: editFirstMessage,
        dynamic_variables_template: parsedVars,
        tools: parsedTools,
      });

      if (syncError) {
        toast.warning(`Saved locally. ElevenLabs sync failed: ${syncError}`);
      } else if (synced) {
        toast.success('Config saved and synced to ElevenLabs');
      } else {
        toast.success('Config saved (ElevenLabs not configured)');
      }
      setEditMode(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to save config');
    } finally {
      setSaving(false);
    }
  };

  if (!config) return <Skeleton className="h-64" />;

  return (
    <div className="space-y-4">
      {/* Sync status banner */}
      {config.last_sync_error && (
        <Card className="border-yellow-500/50 bg-yellow-500/10">
          <CardContent className="py-2 flex items-start gap-2 text-sm text-yellow-700">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>Last ElevenLabs sync failed: {config.last_sync_error}</span>
          </CardContent>
        </Card>
      )}

      {config.last_synced_to_elevenlabs && !config.last_sync_error && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <CheckCircle className="h-3 w-3 text-green-500" />
          Synced to ElevenLabs {new Date(config.last_synced_to_elevenlabs).toLocaleString()}
        </div>
      )}

      {/* Active variant indicator */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Active variant:</span>
        <Badge>{config.active_variant}</Badge>
      </div>

      {/* System Prompt */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">System Prompt</CardTitle>
            {!editMode && (
              <Button variant="ghost" size="sm" onClick={startEdit}>
                <Edit2 className="h-4 w-4 mr-1" /> Edit
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {editMode ? (
            <Textarea
              value={editPrompt}
              onChange={e => setEditPrompt(e.target.value)}
              className="font-mono text-xs"
              rows={12}
              placeholder="You are a sales agent for BuildGravity..."
            />
          ) : (
            <pre className="text-xs font-mono whitespace-pre-wrap text-muted-foreground bg-muted/30 rounded p-3 max-h-64 overflow-y-auto">
              {config.system_prompt || 'No system prompt configured'}
            </pre>
          )}
        </CardContent>
      </Card>

      {/* First Message */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">First Message</CardTitle>
        </CardHeader>
        <CardContent>
          {editMode ? (
            <Input
              value={editFirstMessage}
              onChange={e => setEditFirstMessage(e.target.value)}
              placeholder="Hi, is this {{prospect_name}}?"
            />
          ) : (
            <p className="text-sm text-muted-foreground">
              {config.first_message || 'No first message configured'}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Dynamic Variables */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Dynamic Variables Template</CardTitle>
        </CardHeader>
        <CardContent>
          {editMode ? (
            <Textarea
              value={editDynamicVars}
              onChange={e => setEditDynamicVars(e.target.value)}
              className="font-mono text-xs"
              rows={10}
            />
          ) : (
            <pre className="text-xs font-mono whitespace-pre-wrap text-muted-foreground bg-muted/30 rounded p-3 max-h-48 overflow-y-auto">
              {config.dynamic_variables_template
                ? JSON.stringify(config.dynamic_variables_template, null, 2)
                : 'No dynamic variables template configured'}
            </pre>
          )}
        </CardContent>
      </Card>

      {/* Tools */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Tools (JSON)</CardTitle>
        </CardHeader>
        <CardContent>
          {editMode ? (
            <Textarea
              value={editTools}
              onChange={e => setEditTools(e.target.value)}
              className="font-mono text-xs"
              rows={8}
            />
          ) : (
            <pre className="text-xs font-mono whitespace-pre-wrap text-muted-foreground bg-muted/30 rounded p-3 max-h-48 overflow-y-auto">
              {config.tools?.length
                ? JSON.stringify(config.tools, null, 2)
                : 'No tools configured'}
            </pre>
          )}
        </CardContent>
      </Card>

      {/* Edit mode actions */}
      {editMode && (
        <div className="space-y-2">
          {jsonError && (
            <p className="text-sm text-red-500 flex items-center gap-1">
              <AlertTriangle className="h-4 w-4" /> {jsonError}
            </p>
          )}
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={() => setEditMode(false)} disabled={saving}>
              <X className="h-4 w-4 mr-1" /> Cancel
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving}>
              {saving
                ? <RefreshCw className="h-4 w-4 animate-spin mr-1" />
                : <Save className="h-4 w-4 mr-1" />}
              Save & Sync
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function VoiceAgentConfig() {
  const { user } = useAuth();
  const { config, loading, error, fetchConfig, updateConfig, createVariant, activateVariant } = useAgentConfig();

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  // Access guard: super-admin only
  if (user && !user.is_super_admin) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card className="border-red-500/50 bg-red-500/10">
          <CardContent className="py-6 text-center text-red-600">
            Super-admin access required to view this page.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Mic className="h-6 w-6" /> Voice Agent
          </h1>
          <p className="text-muted-foreground">Manage ElevenLabs agent configuration and review call performance</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchConfig} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <Card className="border-red-500/50 bg-red-500/10">
          <CardContent className="py-3 text-sm text-red-600">{error}</CardContent>
        </Card>
      )}

      <Tabs defaultValue="config">
        <TabsList>
          <TabsTrigger value="config">Agent Config</TabsTrigger>
          <TabsTrigger value="variants">A/B Variants</TabsTrigger>
          <TabsTrigger value="calls">Call History</TabsTrigger>
          <TabsTrigger value="metrics">Performance</TabsTrigger>
        </TabsList>

        {/* Config Tab */}
        <TabsContent value="config" className="mt-4">
          {loading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-40" />)}
            </div>
          ) : (
            <ConfigEditorPanel config={config} onSave={updateConfig} />
          )}
        </TabsContent>

        {/* Variants Tab */}
        <TabsContent value="variants" className="mt-4">
          {loading ? (
            <Skeleton className="h-40" />
          ) : (
            <VariantsPanel
              config={config}
              onCreateVariant={createVariant}
              onActivateVariant={activateVariant}
            />
          )}
        </TabsContent>

        {/* Call History Tab */}
        <TabsContent value="calls" className="mt-4">
          <CallHistoryPanel />
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="metrics" className="mt-4 space-y-4">
          <div>
            <h3 className="text-sm font-medium mb-3">Last 30 Days</h3>
            <MetricsPanel />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
